import { Drawer } from '@mui/material';
import { useCallback, useRef } from 'react';
import type { ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material/styles';
import theme from '../../../../theme/theme';

type SideMenuDrawerProps = {
  variant: 'permanent' | 'persistent' | 'temporary';
  open: boolean;
  onClose?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  drawerWidth: number | string;
  paperSx?: SxProps<Theme> | ((theme: Theme) => SxProps<Theme>);
  children: ReactNode;
};

export function SideMenuDrawer({
  variant,
  open,
  onClose,
  onMouseEnter,
  onMouseLeave,
  drawerWidth,
  paperSx,
  children,
}: SideMenuDrawerProps) {
  const paperRef = useRef<HTMLDivElement | null>(null);
  const extraPaperSx = Array.isArray(paperSx) ? paperSx : paperSx ? [paperSx] : [];

  const handleClose = useCallback<NonNullable<SideMenuDrawerProps['onClose']>>(() => {
    const activeElement = document.activeElement;
    if (activeElement instanceof HTMLElement && paperRef.current?.contains(activeElement)) {
      activeElement.blur();
    }
    onClose?.();
  }, [onClose]);

  return (
    <Drawer
      variant={variant}
      open={open}
      onClose={handleClose}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sx={{
        width: drawerWidth,
        flexShrink: 0,
        transition: `width 300ms cubic-bezier(0.4, 0, 0.2, 1)`,
        '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
      }}
      slotProps={{
        paper: {
          ref: paperRef,
          sx: [
            {
              width: drawerWidth,
              boxSizing: 'border-box',
              top: theme.toolbar.height,
              height: `calc(100% - ${theme.toolbar.height}px)`,
              bgcolor: 'common.white',
              color: 'text.label',
              transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
              overflowX: 'hidden',
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            },
            ...extraPaperSx,
          ] as SxProps<Theme>,
        },
      }}
    >
      {children}
    </Drawer>
  );
}
